/**
 * TrialBanner — Shows 7-day trial countdown for trial users
 * Dismissible, sticky at top
 */
import React, { useState } from 'react';
import { Clock, X, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getBackendBaseUrl } from '../lib/apiBase';

const API_URL = getBackendBaseUrl();

export default function TrialBanner() {
  const { user, token } = useAuth();
  const [dismissed, setDismissed] = useState(() =>
    sessionStorage.getItem('trial_banner_dismissed') === 'true'
  );
  const [loading, setLoading] = useState(false);

  const sub = user?.subscription;
  const isTrial = sub?.is_trial;
  const daysRemaining = sub?.trial_days_remaining ?? sub?.trial_days ?? 7;

  if (!isTrial || dismissed || !user) return null;

  const urgency = daysRemaining <= 1 ? 'critical' : daysRemaining <= 3 ? 'warning' : 'normal';

  const bgColors = {
    critical: 'bg-red-950/80 border-red-500/40',
    warning: 'bg-amber-950/80 border-amber-500/40',
    normal: 'bg-[#00ff88]/5 border-[#00ff88]/30',
  };
  const textColors = {
    critical: 'text-red-400',
    warning: 'text-amber-400',
    normal: 'text-[#00ff88]',
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          package_id: 'pro',
          origin_url: window.location.origin,
          payment_method: 'card',
        }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      console.error('Upgrade failed:', e);
    }
    setLoading(false);
  };

  const handleDismiss = () => {
    sessionStorage.setItem('trial_banner_dismissed', 'true');
    setDismissed(true);
  };

  return (
    <div
      className={`w-full border-b px-4 py-2.5 flex items-center justify-between ${bgColors[urgency]}`}
      data-testid="trial-banner"
    >
      <div className="flex items-center gap-3">
        <Clock className={`w-4 h-4 flex-shrink-0 ${textColors[urgency]}`} />
        <span className="text-sm text-gray-300">
          <span className={`font-bold ${textColors[urgency]}`}>
            {daysRemaining === 0 ? 'Trial expires today' : `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`}
          </span>
          {' '}on your Free Trial.
          {daysRemaining <= 3 && ' Upgrade now to keep access.'}
        </span>
      </div>
      <div className="flex items-center gap-2 ml-4">
        <button
          onClick={handleUpgrade}
          disabled={loading}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all disabled:opacity-50 ${
            urgency === 'normal'
              ? 'bg-[#00ff88] text-black hover:bg-[#00cc6a]'
              : urgency === 'warning'
              ? 'bg-amber-500 text-black hover:bg-amber-400'
              : 'bg-red-500 text-white hover:bg-red-400'
          }`}
          data-testid="trial-upgrade-btn"
        >
          <Zap className="w-3 h-3" />
          {loading ? 'Loading...' : 'Upgrade to Pro — $29/mo'}
        </button>
        <button
          onClick={handleDismiss}
          className="text-gray-600 hover:text-gray-400 transition-colors"
          data-testid="trial-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
